import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

function RedirectWithParams({ to }: { to: string }) {
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) target = target.replace(`:${k}`, v);
  }
  return <Navigate to={target} replace />;
}

/** Look up a profile's username by UUID and 301-redirect to the username URL. */
function RedirectProfileByUserId() {
  const { userId } = useParams();
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) { setTarget("/saljare"); return; }
      const { data } = await (supabase as any)
        .from("public_profiles")
        .select("username")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      setTarget(data?.username ? `/saljare/${data.username}` : "/saljare");
    })();
    return () => { cancelled = true; };
  }, [userId]);
  if (!target) return null;
  return <Navigate to={target} replace />;
}
import { AuthProvider } from "@/contexts/AuthContext";
import { NavigationHistoryProvider } from "@/contexts/NavigationHistoryContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import { SystemAlertBanner } from "./components/SystemAlertBanner";
import { ProfileCompletionGate } from "./components/ProfileCompletionGate";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Marketplace from "./pages/Marketplace";
import ListingDetail from "./pages/ListingDetail";
import Tools from "./pages/Tools";
import About from "./pages/About";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CreateListingLayout from "./pages/CreateListingLayout";
import CreateListingStep1 from "./pages/CreateListingStep1";
import CreateListingStep2 from "./pages/CreateListingStep2";
import CreateListingStep3 from "./pages/CreateListingStep3";
import CreateListingStep4 from "./pages/CreateListingStep4";
import EditProfile from "./pages/EditProfile";
import CreateService from "./pages/CreateService";
import CreateProviderProfile from "./pages/CreateProviderProfile";
import MessageThread from "./pages/MessageThread";
import PublicProfile from "./pages/PublicProfile";
import NotFound from "./pages/NotFound";
import AlertsTest from "./pages/AlertsTest";
import CompleteProfile from "./pages/CompleteProfile";
import AreaCalculatorHub from "./pages/AreaCalculatorHub";
import AreaCalculatorMap from "./pages/AreaCalculatorMap";
import Members from "./pages/Members";
import Admin from "./pages/Admin";
import AdminUploadListing from "./pages/AdminUploadListing";
import EditListing from "./pages/EditListing";

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <NavigationHistoryProvider>
          <SystemAlertBanner />
          <ProfileCompletionGate />
          <main id="main-content">
          <Routes>
            <Route path="/profil-setup" element={<CompleteProfile />} />
            <Route path="/" element={<Index />} />
            <Route path="/skogstjanster" element={<Services />} />
            <Route path="/skogstjanster/:id" element={<ServiceDetail />} />
            <Route path="/annonser" element={<Marketplace />} />
            <Route path="/annonser/:id" element={<ListingDetail />} />
            <Route path="/saljare" element={<Members />} />
            <Route path="/skogsverktyg" element={<Tools />} />
            <Route path="/skogsverktyg/arearaknare" element={<ProtectedRoute><AreaCalculatorHub /></ProtectedRoute>} />
            <Route path="/skogsverktyg/arearaknare/karta" element={<ProtectedRoute><AreaCalculatorMap /></ProtectedRoute>} />
            <Route path="/skogsverktyg/arearaknare/karta/:areaId" element={<ProtectedRoute><AreaCalculatorMap /></ProtectedRoute>} />
            <Route path="/om-oss" element={<About />} />
            <Route path="/villkor" element={<Terms />} />
            <Route path="/integritetspolicy" element={<PrivacyPolicy />} />
            <Route path="/logga-in" element={<Login />} />
            <Route path="/registrera" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><Admin /></ProtectedRoute>} />
            <Route path="/admin/alerts-test" element={<ProtectedRoute requiredRole="admin"><AlertsTest /></ProtectedRoute>} />
            <Route path="/admin/ladda-upp-annons" element={<ProtectedRoute requiredRole="admin"><AdminUploadListing /></ProtectedRoute>} />
            <Route path="/skapa-annons" element={<ProtectedRoute><CreateListingLayout /></ProtectedRoute>}>
              <Route index element={<CreateListingStep1 />} />
              <Route path="detaljer" element={<CreateListingStep2 />} />
              <Route path="platsinfo" element={<CreateListingStep3 />} />
              <Route path="pris" element={<CreateListingStep4 />} />
            </Route>
            <Route path="/annonser/:id/redigera" element={<ProtectedRoute><EditListing /></ProtectedRoute>} />
            <Route path="/redigera-profil" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
            <Route path="/skapa-tjanst" element={<ProtectedRoute requiredAccountType="company"><CreateService /></ProtectedRoute>} />
            <Route path="/skapa-leverantorsprofil" element={<ProtectedRoute requiredAccountType="company"><CreateProviderProfile /></ProtectedRoute>} />
            <Route path="/meddelanden/:threadId" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
            <Route path="/saljare/:username" element={<PublicProfile />} />

            {/* 301 redirects from old paths to new SEO-friendly paths */}
            <Route path="/marknadsplats" element={<Navigate to="/annonser" replace />} />
            <Route path="/marknadsplats/:id" element={<RedirectWithParams to="/annonser/:id" />} />
            <Route path="/marknadsplats/:id/redigera" element={<RedirectWithParams to="/annonser/:id/redigera" />} />
            <Route path="/tjanster" element={<Navigate to="/skogstjanster" replace />} />
            <Route path="/tjanster/:id" element={<RedirectWithParams to="/skogstjanster/:id" />} />
            <Route path="/medlemmar" element={<Navigate to="/saljare" replace />} />
            <Route path="/verktyg" element={<Navigate to="/skogsverktyg" replace />} />
            <Route path="/verktyg/arearaknare" element={<Navigate to="/skogsverktyg/arearaknare" replace />} />
            <Route path="/verktyg/arearaknare/karta" element={<Navigate to="/skogsverktyg/arearaknare/karta" replace />} />
            <Route path="/verktyg/arearaknare/karta/:areaId" element={<RedirectWithParams to="/skogsverktyg/arearaknare/karta/:areaId" />} />
            <Route path="/profil/:userId" element={<RedirectProfileByUserId />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
        </NavigationHistoryProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
